import React, { useMemo, useState } from "react";
import { Box } from "@mui/system";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

/** Matches Mobility Dashboard scrap-analysis donut palette (shifted index). */
export const SCRAP_DONUT_COLORS = [
  "#FF0000",
  "#FA8128",
  "#1CE369",
  "#2AE3D7",
  "#DC59EE",
  "#EED311",
  "#00BFFF",
  "#EF3710",
  "#933",
  "#1B77E4",
  "#E4881B",
  "#3CC39F",
  "#C33C60",
  "#ABBB44",
  "#5444BB",
];

const RADIAN = Math.PI / 180;

function OutsideLabelWithLeader({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  value,
  index,
  payload,
  onValueClick,
  onValueHover,
  onValueLeave,
  isValueHovered = false,
  minPercent = 0.04,
}) {
  if (percent < minPercent) return null;

  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;
  const mx = cx + (outerRadius + 10) * cos;
  const my = cy + (outerRadius + 10) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 16;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";
  const pct = (percent * 100).toFixed(1);
  const label = `${value} (${pct}%)`;

  const clickable = !!onValueClick;
  const tx = ex + (cos >= 0 ? 3 : -3);
  // Rough text width so we can place a generous, easy-to-hit transparent
  // rectangle behind the label instead of relying on the tiny glyphs alone.
  const hitWidth = label.length * 7 + 8;
  const hitX = cos >= 0 ? tx - 4 : tx - hitWidth + 4;

  return (
    <g
      style={{ cursor: clickable ? "pointer" : "default" }}
      onMouseEnter={() => onValueHover?.(index)}
      onMouseLeave={() => onValueLeave?.()}
      onClick={clickable ? () => onValueClick(payload) : undefined}
    >
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke="#9CA3AF"
        fill="none"
        strokeWidth={1}
      />
      {clickable && (
        <rect
          x={hitX}
          y={ey - 10}
          width={hitWidth}
          height={20}
          fill="transparent"
        />
      )}
      <text
        x={tx}
        y={ey}
        dy={4}
        textAnchor={textAnchor}
        fill={clickable && isValueHovered ? "#2563EB" : "#374151"}
        fontSize={12}
        fontFamily="Inter, sans-serif"
      >
        <tspan style={{ textDecoration: isValueHovered ? "underline" : "none" }}>
          {label}
        </tspan>
      </text>
    </g>
  );
}

/**
 * Donut chart for scrap reasons: `reason` / `name` + numeric `value` (count).
 * Reusable shell: same header pattern as CustomBarChart (title, breadcrumbs, filters slot).
 */
export const ScrapReasonsDonutChart = ({
  title,
  subTitle,
  titleTextSize = "24px",
  titleStyle = {},
  subTitleStyle = {},
  breadcrumbs = [],
  breadcrumbStyle = {},
  topRightAction,
  /** Array of { name, value, id?, rawIndex? } */
  data = [],
  colors = SCRAP_DONUT_COLORS,
  colorIndexOffset = 1,
  chartHeight = 460,
  innerRadiusRatio = "32%",
  outerRadiusRatio = "72%",
  cyPercent = "45%",
  onSliceClick,
  onTooltipActionClick,
  minLabelPercent = 0.04,
  boxRadius = "24px",
  noBorder = false,
  noShadow = false,
  boxPadding = { xs: 4, md: 6 },
  boxBorder = "1px solid #E9DFFA",
  centerLabel,
  loading = false,
}) => {
  const [hoveredOuterValueIndex, setHoveredOuterValueIndex] = useState(null);

  const total = useMemo(
    () => data.reduce((s, d) => s + (Number(d.value) || 0), 0),
    [data]
  );

  const centerSpinner = (
    <div
      className="pointer-events-none absolute left-1/2 top-[42%] z-[2] -translate-x-1/2 -translate-y-1/2 text-center"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="ease-linear rounded-full border-4 border-t-4 border-gray-200 h-10 w-10 animate-spin mx-auto"
        style={{ borderTopColor: "rgba(16, 185, 129,1)" }}
      />
    </div>
  );

  const defaultCenter =
    total > 0 ? (
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] z-[1] -translate-x-1/2 -translate-y-1/2 text-center"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="text-lg font-semibold text-gray-700">{total}</div>
        <div className="text-xs text-gray-500">Total tyres</div>
      </div>
    ) : null;

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "white",
        borderRadius: boxRadius,
        boxShadow: noShadow ? "none" : "0 4px 20px rgba(0,0,0,0.05)",
        border: noBorder ? "none" : boxBorder,
        p: noBorder ? 0 : boxPadding,
        position: "relative",
        paddingLeft: "16px",
      }}
    >
      {title != null && (
        <div className="flex flex-wrap flex-col justify-between items-start mb-6 gap-4 px-2">
          <div className="flex-1 w-full">
            <div className="flex flex-row justify-between items-baseline gap-2 mb-4">
              <h2
                style={{
                  fontSize: titleTextSize,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  lineHeight: "100%",
                  color: "#22C55E",
                  ...titleStyle,
                }}
              >
                {title}
                {subTitle && (
                  <span
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "18px",
                      fontWeight: 400,
                      fontStyle: "italic",
                      color: "black",
                      ...subTitleStyle,
                    }}
                  >
                    {subTitle}
                  </span>
                )}
              </h2>
            </div>

            <div className="flex flex-row justify-between items-center w-full min-h-[40px] gap-4">
              <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                {breadcrumbs.map((crumb, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <span
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "13px",
                        lineHeight: "18px",
                        color: "#4841B9",
                        fontStyle: "italic",
                        fontWeight: crumb.active ? 600 : 400,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                        ...breadcrumbStyle,
                      }}
                      className={`transition-colors ${
                        crumb.onClick ? "cursor-pointer hover:opacity-80" : ""
                      }`}
                      onClick={crumb.onClick}
                    >
                      {crumb.label}
                    </span>
                    {index < breadcrumbs.length - 1 && (
                      <span className="text-[#4841B9] font-medium">&gt;</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {topRightAction && (
                  <div className="flex-shrink-0">{topRightAction}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative w-full px-2" style={{ height: chartHeight }}>
        {loading
          ? centerSpinner
          : centerLabel !== undefined
            ? centerLabel
            : defaultCenter}
        {!data.length || total <= 0 ? (
          <div className="flex h-full items-center justify-center text-gray-500 text-sm font-medium">
            {loading ? "" : "No data for the selected filters"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy={cyPercent}
                innerRadius={innerRadiusRatio}
                outerRadius={outerRadiusRatio}
                paddingAngle={0}
                labelLine={false}
                label={(props) => (
                  <OutsideLabelWithLeader
                    {...props}
                    payload={data?.[props.index]}
                    onValueClick={onTooltipActionClick}
                    onValueHover={setHoveredOuterValueIndex}
                    onValueLeave={() => setHoveredOuterValueIndex(null)}
                    isValueHovered={hoveredOuterValueIndex === props.index}
                    minPercent={minLabelPercent}
                  />
                )}
                onClick={
                  onSliceClick
                    ? (a, b) => {
                        let index = typeof b === "number" ? b : undefined;
                        if (index == null && a && typeof a.index === "number") {
                          index = a.index;
                        }
                        if (index == null || index < 0) return;
                        const slice = data[index];
                        if (slice) onSliceClick(slice, index);
                      }
                    : undefined
                }
                style={{ cursor: onSliceClick ? "pointer" : "default" }}
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[(index + colorIndexOffset) % colors.length]}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip
                wrapperStyle={{ pointerEvents: "none" }}
                content={({ payload }) => {
                  const p = payload?.[0]?.payload;
                  if (!p) return null;
                  return (
                    <div
                      style={{
                        background: "rgba(0,0,0,0.82)",
                        padding: "8px 10px",
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      <div className="font-semibold">{p.name}</div>
                      <div>
                        Count: {p.value} (
                        {total > 0
                          ? ((Number(p.value) / total) * 100).toFixed(1)
                          : 0}
                        %)
                      </div>
                    </div>
                  );
                }}
              />
              <Legend
                align="center"
                verticalAlign="bottom"
                height={56}
                wrapperStyle={{
                  fontSize: "12px",
                  fontFamily: "Inter, sans-serif",
                  paddingTop: 8,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Box>
  );
};

export default ScrapReasonsDonutChart;
